import { FC, useEffect } from "react";
import useAuth from "../../../hooks/useAuth";
import { useTranslation } from "react-i18next";
import Button from "../../Button";
import { useHistory, useLocation } from "react-router-dom";
import { Table, TableColumnsType } from "antd";
import config from "../../../lib/config";
import Pagination from "../../common/Pagination";
import useUtil from "../../../hooks/useUtil";
import { IGetPublishers, IPublisher } from "../../../models/publishers.model";
import useAxios from "../../../hooks/useAxios";
import { getPublishers } from "../../../api/publishers.api";
import useLoader from "../../../hooks/useLoader";
import { getPublisherGradeCodes, qsParse } from "../../../lib/util";
import QueryString from "qs";

const defaultParams = {
  pageNo: 1,
  perPage: config.pageSize,
};

const PublisherTable: FC = () => {
  const { writeAuthYn } = useAuth();
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const query = qsParse(location.search);
  const { getCommonCodeLabel } = useUtil();
  const { data: res, isLoading, error, execute } = useAxios<IGetPublishers>();
  useLoader(isLoading, [error]);

  useEffect(() => {
    const params: any = query.pageNo && query.perPage
      ? query
      : defaultParams;
    execute(getPublishers(params));
  }, [location]);

  const renderCreateButton = () => {
    return (
      <Button
        className='btn btn-sm btn-primary btn-w100p'
        text={t('common.button.create')}
        onClick={() => {
          history.push(`/system/publisher/create?${QueryString.stringify(query)}`);
        }}
        writeAuthYn={writeAuthYn}
      />
    );
  };

  const getPageNo = () => {
    return query.pageNo ? Number(query.pageNo) : 1;
  };

  const getPublisherLabel = (record: IPublisher, gradeNum: number) => {
    const gradeCode = getPublisherGradeCodes(record.schoolGbn)[gradeNum - 1];
    const publisherCode = record.publisherDetailResDtoList.find(item => item.gradeCode === gradeCode)?.publisherCode;
    return getCommonCodeLabel('SY0014', publisherCode) || '-';
  };

  const columns: TableColumnsType<IPublisher> = [
    {
      title: t('system.publisher.5'),
      dataIndex: 'rowNo',
      sorter: (a, b) => a.rowNo - b.rowNo,
    },
    {
      title: t('system.publisher.6'),
      dataIndex: 'schoolGbn',
      sorter: (a, b) => a.schoolGbn?.localeCompare(b.schoolGbn),
      render: (value) => getCommonCodeLabel('SY0013', value),
    },
    {
      title: t('system.publisher.7'),
      dataIndex: 'schoolKorName',
      sorter: (a, b) => a.schoolKorName?.localeCompare(b.schoolKorName),
    },
    {
      title: t('system.publisher.2'),
      sorter: (a, b) => a.publisherDetailResDtoList[0]?.publisherCode?.localeCompare(b.publisherDetailResDtoList[0]?.publisherCode),
      render: (_, record) => getPublisherLabel(record, 1),
    },
    {
      title: t('system.publisher.3'),
      sorter: (a, b) => a.publisherDetailResDtoList[1]?.publisherCode?.localeCompare(b.publisherDetailResDtoList[1]?.publisherCode),
      render: (_, record) => getPublisherLabel(record, 2),
    },
    {
      title: t('system.publisher.4'),
      sorter: (a, b) => a.publisherDetailResDtoList[2]?.publisherCode?.localeCompare(b.publisherDetailResDtoList[2]?.publisherCode),
      render: (_, record) => getPublisherLabel(record, 3),
    },
    {
      title: t('system.publisher.8'),
      dataIndex: 'modifierKorName',
      sorter: (a, b) => a.modifierKorName?.localeCompare(b.modifierKorName),
    },
  ];

  return (
    <>
      <div className="row mt-4">
        <div className="col-6 pr-t10">
          {t('common.text.count')}: {res?.total}{t('common.text.unit')}
        </div>
        <div className="col-6 text-right">
          {renderCreateButton()}
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <Table
            columns={columns}
            dataSource={res?.data}
            pagination={{
              current: getPageNo(),
              pageSize: config.pageSize
            }}
            onRow={record => {
              return {
                onClick: () => {
                  history.push(`/system/publisher/${record.schoolCode}/edit?${QueryString.stringify(query)}`);
                }
              };
            }}
            rowClassName="pointer"
          />
        </div>
      </div>
      <div className="row mt-1">
        <div className="col-3" />
        <div className="col-6">
          <Pagination
            page={query.pageNo}
            total={res?.total}
            url="/system/publisher"
          />
        </div>
        <div className="col-3 text-right">
          {renderCreateButton()}
        </div>
      </div>
    </>
  );
};

export default PublisherTable;